import React, {useState} from 'react';
import clsx from 'clsx';
import {
    Dialog,
    DialogContent,
    Grid,
} from '@mui/material';
import {GradientColorHeader} from '@Local/Components/Dialog/';
import {EditableInfo} from '@Local/Components';
import {ProductCard} from '@Local/Components/InfoCard';
import { Box, Button, CheckboxField } from '@curves/ui-base';

import {formatCurrencyFn, isFunction, localUser} from '@Util/utils';
import {parseDate} from '@Util/moment.util';
import useOrderReturnApi from '@Apis/useOrderReturnApi';
import OverviewListTable from '@Local/Components/OverviewList/OverviewListTable';
import {DATE_TIME_STANDARD} from '@Constants/appConfig';
// 建立折讓項目表格的 header 配置
const headerRow = [
    {
        cellKey: "product",
        headerLabel: "品項",
        minWidth: 200,
        className: "vertical-align-middle",
        formatCell: (_v, row) => <ProductCard productName={row.itemName}/>,
    },
    {
        cellKey: "unitPrice",
        headerLabel: "金額",
        align: "right",
        minWidth: 120,
        className: "vertical-align-middle",
        formatCell: (_v, row) => formatCurrencyFn(row.itemPrice),
    },
    {
        cellKey: "quantity",
        headerLabel: "數量",
        align: "center",
        minWidth: 100,
        className: "vertical-align-middle",
        formatCell: (_v, row) => (row.itemCount),
    },
    {
        cellKey: "total",
        headerLabel: "合計",
        align: "right",
        minWidth: 160,
        className: "vertical-align-middle",
        formatCell: (_v, row) => formatCurrencyFn(row.itemPrice * row?.itemCount),
    },
];
const OrderAllowanceCancelDialog = React.forwardRef(({
    open = false,
    onClose,
    refresh,
    sourceData = {},
}, ref) => {
    if (!open) return null;
    // TODO: 假資料 - 後端 API 建置完成後需移除
    const mockData = {
        allowanceID: "AL20250116001",
        invoiceNo: "AB12345678",
        invoiceDate: "2025-01-15T10:30:00.000Z",
        allowanceAmount: 1200,
        allowanceDate: "2025-01-16T14:20:00.000Z",
        allowanceStatusText: "有效",
        items: [
            {
                itemName: "Curves 30分鐘循環訓練課程",
                itemPrice: 500,
                itemCount: 2,
            },
            {
                itemName: "個人教練指導服務",
                itemPrice: 200,
                itemCount: 1,
            }
        ]
    };
    // 使用假資料或傳入的資料
    const data = Object.keys(sourceData).length > 0 ? sourceData : mockData;
    const {allowanceID, invoiceNo, invoiceDate, allowanceAmount, allowanceDate, allowanceStatusText, items = []} = data;
    const {userID} = localUser.get();
    const [isConfirmed, setIsConfirmed] = useState(false);
    const {postOrderReturnInvalidAllowanceConfirmApi} = useOrderReturnApi();
    const doPostOrderReturnInvalidAllowanceConfirmApi = async (params) => {
        try {
            const resp = await postOrderReturnInvalidAllowanceConfirmApi(params);
            if (resp) {
                isFunction(refresh) && refresh();
                isFunction(onClose) && onClose();
            }
        } catch (error) {
            console.error('取消折讓失敗:', error);
        }
    };
    const handleSubmit = () => {
        if (!isConfirmed) return;
        const params = {
            allowanceID,
            employeeID: userID,
        }
        doPostOrderReturnInvalidAllowanceConfirmApi(params);
    }
    return (
        <Dialog
            className={clsx('simple-content', 'dialog')}
            PaperProps={{className: 'wx-65rem'}}
            open={open}
            fullWidth
        >
            <GradientColorHeader title="取消折讓" onClose={onClose}/>
            <DialogContent className={'container main-container-spacing bg-pager'}>
                <Grid spacing={1} container>
                    {/* 折讓資訊 */}
                    <Grid xs={6} item>
                        <EditableInfo label="折讓單號">
                            {allowanceID || '無相關資料'}
                        </EditableInfo>
                    </Grid>
                    <Grid xs={6} item>
                        <EditableInfo label="發票號碼">
                            {invoiceNo || '無相關資料'}
                        </EditableInfo>
                    </Grid>
                    <Grid xs={6} item>
                        <EditableInfo label="折讓金額">
                            {formatCurrencyFn(allowanceAmount || 0, {symbol: '$'})}
                        </EditableInfo>
                    </Grid>
                    <Grid xs={6} item>
                        <EditableInfo label="折讓日期">
                            {parseDate(allowanceDate, DATE_TIME_STANDARD.DISPLAYFULLBYSLASH)}
                        </EditableInfo>
                    </Grid>
                    <Grid xs={6} item>
                        <EditableInfo label="發票日期">
                            {parseDate(invoiceDate, DATE_TIME_STANDARD.DISPLAYFULLBYSLASH)}
                        </EditableInfo>
                    </Grid>
                    <Grid xs={6} item>
                        <EditableInfo label="折讓狀態">
                            {allowanceStatusText || '無相關資料'}
                        </EditableInfo>
                    </Grid>
                </Grid>
                {/* 折讓項目表格 - 獨立顯示 */}
                {items && items.length > 0 && (
                    <Box className={'mt-2'}>
                        <OverviewListTable
                            className="allowance-items-table"
                            headerRow={headerRow}
                            list={items}
                            isGray={true}
                        />
                    </Box>
                )}
                {/* 確認勾選 */}
                <Box className={'mt-3'}>
                    <CheckboxField>
                        <CheckboxField.Checkbox
                            value={true}
                            label="我確認要取消這筆折讓單，此操作無法復原"
                            checked={isConfirmed}
                            onChange={(e) => setIsConfirmed(e.target.checked)}
                        />
                    </CheckboxField>
                </Box>
                {/* 送出按鈕 */}
                <Box className={'text-right mt-3'}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={!isConfirmed}
                    >
                        確認取消
                    </Button>
                </Box>
            </DialogContent>
        </Dialog>
    );
});
export default OrderAllowanceCancelDialog;
